import { useState } from 'react';

import type { OperationVariables, UseQuery } from '../../types';
import { publicAppSearchQuery } from './publicAppSearchQuery';

type AppImages = {
  url_128x128: string;
  url_64x64: string;
  url_32x32: string;
  url_16x16: string;
};

type App = {
  id: string;
  canonicalId: string;
  serviceId: string;
  slug: string;
  name: string;
  images: AppImages;
  currentImplementation: {
    id: string;
  };
};

type publicAppSearchQueryVariables = {
  nameContains: string;
  limit: number;
  excludeUpcoming?: boolean;
  includeIsAppAdmin?: boolean;
  includeIsAppCollaborator?: boolean;
};

type options = {
  /** limit */
  limit?: number;
  /** searchDebounceDelay */
  searchDebounceDelay?: number;
  /** Excludes upcoming apps from search results */
  excludeUpcoming?: boolean;
  /** The `useQuery` hook to use. */
  useQuery: UseQuery;
  /** Toggles the inclusion of services where the authenticated user is associated with an app as an admin */
  includeIsAppAdmin?: boolean;
  /** Toggles the inclusion of services where the authenticated user is associated with an app as a collaborator */
  includeIsAppCollaborator?: boolean;
};

type useAppSearchDataOutput = OperationVariables & {
  onSearch: (query: string) => void;
  searchQuery: string;
  apps: App[];
};

const DEFAULT_LIMIT = 20;

const timeoutIds = new WeakMap();
const simpleDebounce = (fn: Function, delay: number) => (...args: any) => {
  clearTimeout(timeoutIds.get(fn));
  const timeoutId = setTimeout(() => {
    fn(...args);
  }, delay);
  timeoutIds.set(fn, timeoutId);
};

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/(\s+)/g, ' ').trim();

/**
 * Provides the ability to search for Zapier apps (services, integrations)
 * and receive a set of matching results.
 */
export const useAppSearchData = ({
  limit,
  searchDebounceDelay,
  excludeUpcoming,
  useQuery,
  includeIsAppAdmin,
  includeIsAppCollaborator,
}: options): useAppSearchDataOutput => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const variables: publicAppSearchQueryVariables = {
    limit: limit || DEFAULT_LIMIT,
    // NOTE: in addition to trimming leading and trailing whitespace, the regex
    // below replaces sequences of two or more whitespaces (including tabs/
    // linefeed/carriage-return with a single space -- this should help even when
    // users paste their query into the input
    nameContains: dedupeAndTrimWhitespace(searchQuery),
    excludeUpcoming,
    includeIsAppAdmin,
    includeIsAppCollaborator,
  };

  const { data, ...graphqlQueryControls } = useQuery(publicAppSearchQuery, {
    variables,
    skip: searchQuery.trim() === '',
  });

  const apps: App[] = data?.getAppsByNameContains?.edges || [];

  return {
    ...graphqlQueryControls,
    onSearch: simpleDebounce((query: string) => {
      setSearchQuery(dedupeAndTrimWhitespace(query));
    }, searchDebounceDelay || 0),
    searchQuery: dedupeAndTrimWhitespace(searchQuery),
    apps,
  };
};
